import React from 'react';
import { cn } from '../../lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'success';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'icon';
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ...props
  }, ref) => {

    const baseClasses = "inline-flex items-center justify-center rounded-lg font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#13EC5B]/50 focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 active:scale-95";

    const variantClasses = {
      primary: "bg-[#13EC5B] text-black hover:bg-[#10D450] shadow-lg shadow-[#13EC5B]/20 hover:shadow-[#13EC5B]/30",
      secondary: "bg-white/10 text-white hover:bg-white/15 border border-white/10",
      outline: "border border-white/20 bg-transparent hover:bg-white/5 text-gray-200 hover:text-white hover:border-white/30",
      ghost: "hover:bg-white/10 text-gray-300 hover:text-white",
      destructive: "bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30",
      success: "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30",
    };

    const sizeClasses = {
      sm: "h-9 px-3 text-sm",
      md: "h-10 px-4 py-2 text-sm",
      lg: "h-12 px-6 py-3 text-base",
      xl: "h-14 px-8 py-4 text-lg",
      icon: "h-10 w-10",
    };

    const widthClasses = fullWidth ? "w-full" : "w-auto";

    return (
      <button
        className={cn(baseClasses, variantClasses[variant], sizeClasses[size], widthClasses, className)}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        {!loading && leftIcon && <span className="mr-2">{leftIcon}</span>}
        {children}
        {!loading && rightIcon && <span className="ml-2">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button };
